import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AIChatWidget from './AIChatWidget';
import ReminderWidget from './ReminderWidget';
import NotificationBell from './NotificationBell';
import NotificationToast from './NotificationToast';
import { NoteAnalysisProvider, useNoteAnalysis } from '../contexts/NoteAnalysisContext';
import NoteSuggestionsPopup from './NoteSuggestionsPopup';
import {
  LayoutDashboard, Search, Users, Settings, LogOut,
  ChevronLeft, ChevronRight, Building2, Sparkles, Bot,
  Menu, X, BarChart3, TrendingUp, GitBranch, StickyNote, FileText, Newspaper, Trophy, Cpu
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', adminOnly: false },
  { to: '/leads', icon: Search, label: 'Lead Intelligence', adminOnly: false },
  { to: '/it-strucnjaci', icon: Cpu, label: 'IT stručnjaci', adminOnly: false },
  { to: '/crm', icon: Building2, label: 'CRM', adminOnly: false },
  { to: '/faze-lidova', icon: GitBranch, label: 'Faze lidova', adminOnly: false },
  { to: '/ploca', icon: StickyNote, label: 'Ploča', adminOnly: false },
  { to: '/ponude', icon: FileText, label: 'Ponude', adminOnly: false },
  { to: '/trziste', icon: Newspaper, label: 'Tržište', adminOnly: false },
  { to: '/natjecaji', icon: Trophy, label: 'Natječaji', adminOnly: false },
  { to: '/statistics', icon: TrendingUp, label: 'Statistika', adminOnly: false },   // svi vide SVOJU statistiku (backend self-scope)
  { to: '/ai-automation', icon: Bot, label: 'AI Automation', adminOnly: false },
  { to: '/cold-email', icon: Sparkles, label: 'Cold Email', adminOnly: false },
  { to: '/admin', icon: Settings, label: 'Admin', adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // ═══ NOVA VERZIJA — otvoren tab vrti stari kod dok se ne osvježi ═══
  // (10.–11.9.2026: "Ručno zaduži" nije se vidjelo, "Pokreni sada" slao je stari zahtjev.)
  // Svakih 5 min + pri povratku na tab: usporedi main.*.js na serveru s učitanim.
  const [newVersion, setNewVersion] = useState(false);
  useEffect(() => {
    const loaded = (document.querySelector('script[src*="/static/js/main."]') || {}).src || '';
    const cur = (loaded.match(/main\.[a-z0-9]+\.js/) || [])[0];
    if (!cur) return;
    const check = async () => {
      try {
        const html = await (await fetch('/?v=' + Date.now(), { cache: 'no-store' })).text();
        const srv = (html.match(/main\.[a-z0-9]+\.js/) || [])[0];
        if (srv && srv !== cur) setNewVersion(true);
      } catch { /* offline — tiho */ }
    };
    const iv = setInterval(check, 5 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // ═══ HEARTBEAT — mjeri aktivno prisustvo na platformi ═══
  // Ping svakih 60s, SAMO ako: tab je vidljiv I korisnik nije idle (<2 min od zadnje akcije).
  // Idle = nema mouse/keyboard/scroll/touch eventa. Tako se ne broji otvoren-ali-napušten tab.
  useEffect(() => {
    if (!user) return;
    let lastActivity = Date.now();
    const markActive = () => { lastActivity = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, markActive, { passive: true }));

    const IDLE_MS = 2 * 60 * 1000;  // 2 min bez akcije = idle
    const sendHeartbeat = () => {
      const visible = document.visibilityState === 'visible';
      const active = (Date.now() - lastActivity) < IDLE_MS;
      if (visible && active) {
        api.post('/activity/heartbeat').catch(() => {});  // fire-and-forget
      }
    };
    // Prvi ping odmah (ako je aktivan), pa svakih 30s.
    // 30s (a ne 60s) da drift/kašnjenje ne preskoči cijelu minutu — backend
    // svejedno deduplicira na minute-bucket (ON CONFLICT), pa nema overcounta.
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30 * 1000);

    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, markActive));
    };
  }, [user]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.statsAccess && user?.role !== 'admin' && !user?.can_view_stats) return false;
    return true;
  });

  // Split nav items into sections
  const mainItems = visibleItems.filter(i => ['/', '/leads', '/it-strucnjaci', '/crm', '/faze-lidova', '/ploca', '/ponude', '/trziste', '/natjecaji'].includes(i.to));
  const toolItems = visibleItems.filter(i => ['/statistics', '/ai-automation', '/cold-email', '/auto-enrich', '/admin'].includes(i.to));

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className={cn(
        "flex items-center gap-3 px-5 py-5",
        !isMobile && collapsed && "justify-center px-0"
      )} style={{ borderBottom: '1px solid #2E2D2A' }}>
        <div className="w-8 h-8 rounded-lg gradient-gold flex items-center justify-center">
          <Sparkles size={16} className="text-white" />
        </div>
        {(isMobile || !collapsed) && (
          <div className="overflow-hidden flex-1">
            <div className="text-[15px] font-bold text-white" style={{ letterSpacing: '-0.3px' }}>People <span style={{ color: '#C17F0A' }}>Pons</span></div>
            <div className="text-[10px] font-semibold uppercase" style={{ color: '#6B6860', letterSpacing: '0.5px' }}>Lead Intelligence</div>
          </div>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" style={{ color: '#6B6860' }}>
            <X size={20} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2.5 overflow-y-auto">
        {/* GLAVNO section */}
        {(isMobile || !collapsed) && (
          <div className="px-3 pt-2 pb-1.5 text-[10px] font-semibold uppercase" style={{ color: '#5A5850', letterSpacing: '0.5px' }}>Glavno</div>
        )}
        <div className="space-y-0.5">
          {mainItems.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150",
                !isMobile && collapsed && "justify-center px-0",
                isActive ? "text-white" : "hover:text-stone-300"
              )}
              style={({ isActive }) => isActive ? { background: '#332A14', color: '#FFFFFF' } : { color: '#8A8780' }}
            >
              {({ isActive }) => (
                <>
                  <Icon size={17} style={{ color: isActive ? '#C17F0A' : '#6B6860' }} className="shrink-0" />
                  {(isMobile || !collapsed) && <span>{label}</span>}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* ALATI section */}
        {toolItems.length > 0 && (
          <>
            {(isMobile || !collapsed) && (
              <div className="px-3 pt-5 pb-1.5 text-[10px] font-semibold uppercase" style={{ color: '#5A5850', letterSpacing: '0.5px' }}>Alati</div>
            )}
            <div className="space-y-0.5">
              {toolItems.map(({ to, icon: Icon, label }) => (
                <NavLink key={to} to={to}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150",
                    !isMobile && collapsed && "justify-center px-0",
                    isActive ? "text-white" : "hover:text-stone-300"
                  )}
                  style={({ isActive }) => isActive ? { background: '#332A14', color: '#FFFFFF' } : { color: '#8A8780' }}
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={17} style={{ color: isActive ? '#C17F0A' : '#6B6860' }} className="shrink-0" />
                      {(isMobile || !collapsed) && <span>{label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User & Collapse */}
      <div className="p-3 space-y-2" style={{ borderTop: '1px solid #2E2D2A' }}>
        {(isMobile || !collapsed) && (
          <NavLink to="/profile" className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-stone-800 transition-colors" title="Profil">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white" style={{ background: '#332A14' }}>
              {(user?.display_name || user?.username || 'U')[0].toUpperCase()}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-white">{user?.display_name || user?.username}</div>
              <div className="text-[10px] uppercase font-medium" style={{ color: '#6B6860', letterSpacing: '0.5px' }}>{user?.role} · profil</div>
            </div>
          </NavLink>
        )}
        <button onClick={handleLogout}
          className={cn(
            "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[13px] transition-all",
            !isMobile && collapsed && "justify-center px-0"
          )} style={{ color: '#6B6860' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#2E2D2A'; e.currentTarget.style.color = '#D4D0C8'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = '#6B6860'; }}>
          <LogOut size={15} /> {(isMobile || !collapsed) && 'Odjava'}
        </button>
        {!isMobile && (
          <button onClick={() => setCollapsed(c => !c)}
            className="flex items-center justify-center w-full py-1" style={{ color: '#5A5850' }}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        )}
      </div>
    </>
  );

  return (
    <NoteAnalysisProvider>
    <div className="flex h-screen overflow-hidden">
      {/* ═══ MOBILE HEADER BAR ═══ */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-3"
          style={{ background: 'linear-gradient(180deg, #1E1D1B 0%, #1A1918 100%)' }}>
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2.5 flex-1">
            <div className="w-7 h-7 rounded-lg gradient-gold flex items-center justify-center shadow-lg">
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="text-sm font-bold text-white tracking-tight">Lead Intel</span>
          </div>
          <NotificationBell />
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">
            {user?.display_name || user?.username}
          </div>
        </div>
      )}

      {/* ═══ MOBILE OVERLAY ═══ */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ═══ SIDEBAR ═══ */}
      {isMobile ? (
        <aside className={cn(
          "fixed top-0 left-0 bottom-0 z-[70] w-[280px] flex flex-col transition-transform duration-300 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )} style={{
          background: 'linear-gradient(180deg, #1E1D1B 0%, #1A1918 50%, #161513 100%)'
        }}>
          {sidebarContent}
        </aside>
      ) : (
        <aside className={cn(
          "flex flex-col transition-all duration-300 ease-out relative shrink-0",
          collapsed ? "w-[68px]" : "w-[220px]"
        )} style={{
          background: 'linear-gradient(180deg, #1E1D1B 0%, #1A1918 50%, #161513 100%)'
        }}>
          {sidebarContent}
        </aside>
      )}

      {/* ═══ MAIN ═══ */}
      <main className={cn(
        "flex-1 overflow-hidden bg-[hsl(var(--background))] relative",
        isMobile && "pt-[56px]"
      )}>
        {/* Desktop notification bell — floating top-right, no topbar */}
        {!isMobile && (
          <div style={{ position: 'absolute', top: '8px', right: '16px', zIndex: 40 }}>
            <NotificationBell />
          </div>
        )}
        {newVersion && (
            <div onClick={() => window.location.reload()} role="button"
              style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, cursor: 'pointer',
                background: '#C17F0A', color: '#FFF', padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                boxShadow: '0 6px 20px rgba(0,0,0,0.18)' }}>
              ✨ Dostupna je nova verzija platforme — klikni za osvježavanje
            </div>
          )}
          <Outlet />
      </main>

      {/* ═══ REMINDER WIDGET ═══ */}
      <ReminderWidget />

      {/* ═══ AI CHAT WIDGET ═══ */}
      <AIChatWidget />

      {/* ═══ NOTIFICATION TOASTS ═══ */}
      <NotificationToast />

      {/* ═══ AI COPILOT — globalni popup (preživi navigaciju) ═══ */}
      <GlobalNotePopup />
    </div>
    </NoteAnalysisProvider>
  );
}

// Globalni popup s AI prijedlozima — renderira se iznad cijele app, neovisno o stranici.
function NoteToast({ toast }) {
  const cfg = ({
    loading: { bg: '#1C1917', label: `Primjenjujem za ${toast.company}…` },
    success: { bg: '#15803D', label: `✓ Primijenjeno za ${toast.company}${toast.count ? ` · ${toast.count} ${toast.count === 1 ? 'akcija' : 'akcije'}` : ''}` },
    error:   { bg: '#DC2626', label: `Nije uspjelo za ${toast.company} — popup se vratio, pokušaj ponovno` },
  })[toast.type] || { bg: '#1C1917', label: '' };
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 400, background: cfg.bg, color: '#fff',
      padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
      maxWidth: 380 }}>
      {cfg.label}
    </div>
  );
}

function GlobalNotePopup() {
  const { queue, statusConfig, dismiss, applyOptimistic, toast, snoozedIds, snooze, wakeAll } = useNoteAnalysis();
  const active = (queue || []).filter(x => !snoozedIds?.has(x.id));
  const snoozedCnt = (queue || []).length - active.length;
  const cur = active.length > 0 ? active[0] : null;
  return (
    <>
      {cur && (
        <NoteSuggestionsPopup
          key={cur.id}
          data={cur}
          statusConfig={statusConfig}
          onClose={() => dismiss(cur.id)}
          onSnooze={() => snooze(cur.id)}
          onApply={(approved) => applyOptimistic(cur, approved)}
        />
      )}
      {/* odgođeni AI prijedlozi — čip na dnu, klik ih vraća; ništa se ne gubi */}
      {!cur && snoozedCnt > 0 && (
        <button onClick={wakeAll}
          style={{ position: 'fixed', bottom: '18px', left: '50%', transform: 'translateX(-50%)', zIndex: 290,
                   display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 20px', borderRadius: '999px',
                   background: 'linear-gradient(135deg,#C17F0A,#A86D00)', color: '#fff', border: 'none', cursor: 'pointer',
                   fontFamily: 'inherit', fontSize: '13px', fontWeight: 800, boxShadow: '0 8px 28px rgba(120,80,10,0.45)' }}>
          ✨ AI prijedlozi čekaju odluku ({snoozedCnt}) — klikni za prikaz
        </button>
      )}
      {toast && <NoteToast toast={toast} />}
    </>
  );
}
